import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import Logo from '@/assets/gettingStarted.svg?react'

interface GettingStartedProps {
  /** Background color for the CTA section */
  ctaBgColor?: string
}

const GettingStarted: React.FC<GettingStartedProps> = ({
  ctaBgColor = '#00C5FB',
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  })

  // Logo zoom & fade
  const logoScale   = useTransform(scrollYProgress, [0, 0.8],     [0.4, 10])
  const logoOpacity = useTransform(scrollYProgress, [0.7, 0.95], [1,    0])

  // CTA fades in and then sticks at full opacity
  const ctaOpacity = useTransform(
    scrollYProgress,
    [0.75, 0.85, 1],
    [0,    1,    1]
  )


  const containerPadding      = useTransform(
    scrollYProgress,
    [0.85, 0.90, 1],
    ['0rem', '0rem', '1.5rem']
  )
  const containerRadius       = useTransform(
    scrollYProgress,
    [0.85, 0.90, 1],
    ['0rem', '0rem', '1rem']
  )
  const containerMaxWidth     = useTransform(
    scrollYProgress,
    [0.85, 0.90, 1],
    ['100%', '100%', '80rem']
  )
  const containerMarginBottom = useTransform(
    scrollYProgress,
    [0.85, 0.90, 1],
    ['0rem', '0rem', '5rem']
  )

  return (
    <section
      ref={sectionRef}
      className="tw-h-[250vh] tw-relative"
    >
      <motion.div
        style={{
          borderBottomLeftRadius: containerRadius,
          borderBottomRightRadius: containerRadius,
          paddingLeft: containerPadding,
          paddingRight: containerPadding,
          marginBottom: containerMarginBottom,
          maxWidth: containerMaxWidth,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
        className="tw-sticky tw-top-0 tw-h-screen tw-overflow-hidden tw-z-10"
      >
        {/* Logo Zoom */}
        <motion.div
          style={{ scale: logoScale, opacity: logoOpacity }}
          className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-origin-center"
        >
          <Logo
            className="tw-w-[300px] md:tw-w-[500px] tw-h-auto"
            style={{ color: ctaBgColor }}
          />
        </motion.div>

        {/* CTA Section */}
        <motion.div
          style={{ opacity: ctaOpacity, backgroundColor: ctaBgColor }}
          className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-rounded-inherit"
        >
          <h2 className="tw-text-black tw-text-[32px] md:tw-text-[56px] tw-font-bold tw-mb-6">
            Ready to get started?
          </h2>
          <Link
            to="/schedule-demo"
            className="tw-font-semibold tw-py-3 tw-px-6 tw-rounded-full tw-bg-[#111111] tw-text-white hover:tw-bg-white hover:tw-text-black tw-transition-transform tw-duration-700"
          >
            Schedule a Demo
          </Link>
        </motion.div>
      </motion.div>

      {/* Spacer to unstick smoothly */}
      <div className="tw-h-screen" />
    </section>
  )
}

export default GettingStarted
